/**
 * AIInterviewCoachHome — /coach
 *
 * Public landing page for the AI Interview Coach (BIAE).
 * Mirrors the *Home pattern used by Mastery, Resume, Portfolio etc. so the
 * AppRail icon lands cold visitors on a marketing surface instead of the
 * auth-walled /evaluate work surface.
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Trans, useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { FREE_EVALS_PER_MONTH } from "../constants/pricing";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

function scoreColor(score: number, theme: Theme): string {
  if (score >= 4) return theme.palette.primary.main;
  if (score === 3) return theme.palette.warning.main;
  return theme.palette.error.main;
}

const SCORING_DIMENSION_KEYS = [
  {
    title: "AIInterviewCoachHome.scoringDimensionSituationClarityTitle",
    body: "AIInterviewCoachHome.scoringDimensionSituationClarityBody",
  },
  {
    title: "AIInterviewCoachHome.scoringDimensionTaskOwnershipTitle",
    body: "AIInterviewCoachHome.scoringDimensionTaskOwnershipBody",
  },
  {
    title: "AIInterviewCoachHome.scoringDimensionActionSpecificityTitle",
    body: "AIInterviewCoachHome.scoringDimensionActionSpecificityBody",
  },
  {
    title: "AIInterviewCoachHome.scoringDimensionResultQuantificationTitle",
    body: "AIInterviewCoachHome.scoringDimensionResultQuantificationBody",
  },
  {
    title: "AIInterviewCoachHome.scoringDimensionReflectionDepthTitle",
    body: "AIInterviewCoachHome.scoringDimensionReflectionDepthBody",
  },
  {
    title: "AIInterviewCoachHome.scoringDimensionCompanyAlignmentTitle",
    body: "AIInterviewCoachHome.scoringDimensionCompanyAlignmentBody",
  },
] as const;

// Sample-evaluation content is static marketing copy — translated via the
// key-map pattern. `dimensions[].nameKey` reuses SCORING_DIMENSION_KEYS'
// title keys 1:1 (identical English text) to avoid duplicate strings.
// `targetCompany` is left as a plain (untranslated) proper noun, like a
// config value, and interpolated into the "Calibrated for" sentence.
const SAMPLE_EVALUATION = {
  questionKey: "AIInterviewCoachHome.sampleEvalQuestion",
  targetCompany: "Anthropic",
  evaluatedInSeconds: 28,
  hiringDecisionKey: "AIInterviewCoachHome.sampleEvalHiringDecision",
  hiringSubtextKey: "AIInterviewCoachHome.sampleEvalHiringSubtext",
  answerExcerptKey: "AIInterviewCoachHome.sampleEvalAnswerExcerpt",
  dimensions: [
    {
      nameKey: SCORING_DIMENSION_KEYS[0].title,
      score: 4,
      feedbackKey: "AIInterviewCoachHome.sampleEvalDimension1Feedback",
    },
    {
      nameKey: SCORING_DIMENSION_KEYS[1].title,
      score: 5,
      feedbackKey: "AIInterviewCoachHome.sampleEvalDimension2Feedback",
    },
    {
      nameKey: SCORING_DIMENSION_KEYS[2].title,
      score: 3,
      feedbackKey: "AIInterviewCoachHome.sampleEvalDimension3Feedback",
    },
    {
      nameKey: SCORING_DIMENSION_KEYS[3].title,
      score: 4,
      feedbackKey: "AIInterviewCoachHome.sampleEvalDimension4Feedback",
    },
    {
      nameKey: SCORING_DIMENSION_KEYS[4].title,
      score: 2,
      feedbackKey: "AIInterviewCoachHome.sampleEvalDimension5Feedback",
    },
    {
      nameKey: SCORING_DIMENSION_KEYS[5].title,
      score: 4,
      feedbackKey: "AIInterviewCoachHome.sampleEvalDimension6Feedback",
    },
  ],
  takeawayKey: "AIInterviewCoachHome.sampleEvalTakeaway",
} as const;

const FEATURE_KEYS = [
  {
    title: "AIInterviewCoachHome.featureAiScoredMockInterviewsTitle",
    body: "AIInterviewCoachHome.featureAiScoredMockInterviewsBody",
  },
  {
    title: "AIInterviewCoachHome.featureCompanySpecificCalibrationTitle",
    body: "AIInterviewCoachHome.featureCompanySpecificCalibrationBody",
  },
  {
    title: "AIInterviewCoachHome.featureQuestionBankTitle",
    body: "AIInterviewCoachHome.featureQuestionBankBody",
  },
  {
    title: "AIInterviewCoachHome.featureVoiceOrTextInputTitle",
    body: "AIInterviewCoachHome.featureVoiceOrTextInputBody",
  },
] as const;

export default function AIInterviewCoachHome() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation("pages-core");

  const primaryCta = isAuthenticated
    ? { label: t("AIInterviewCoachHome.primaryCtaAuthenticatedLabel"), href: "/evaluate" }
    : { label: t("AIInterviewCoachHome.primaryCtaUnauthenticatedLabel"), href: "/login" };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="flex-start">
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: "0.95rem !important", color: `${theme.palette.primary.main} !important` }} />}
              label={t("AIInterviewCoachHome.heroChipLabel")}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.08em",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h2"
              fontWeight={500}
              sx={{
                letterSpacing: "-0.03em",
                fontSize: { xs: "2.4rem", md: "3.2rem" },
                maxWidth: 900,
              }}
            >
              <Trans
                i18nKey="AIInterviewCoachHome.heroTitleLine1"
                t={t}
                components={[
                  <Box key="span0" component="span" sx={{ color: theme.palette.primary.main }} />,
                ]}
              />
              <br />
              {t("AIInterviewCoachHome.heroTitleLine2")}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400, maxWidth: 780, lineHeight: 1.5 }}
            >
              {t("AIInterviewCoachHome.heroSubtitle")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() =>
                  navigate(
                    primaryCta.href,
                    primaryCta.href === "/login"
                      ? { state: { from: "/evaluate" } }
                      : undefined
                  )
                }
                sx={{
                  bgcolor: theme.palette.primary.main,
                  fontWeight: 500,
                  px: 3,
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                }}
              >
                {primaryCta.label}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/pricing")}
                sx={{
                  fontWeight: 500,
                  px: 3,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: theme.palette.primary.main,
                  "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                {t("AIInterviewCoachHome.seePricingButton")}
              </Button>
            </Stack>
            <Stack direction="row" spacing={3} mt={3} flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="body2" color="text.secondary">
                  {t("AIInterviewCoachHome.trustChipSixDimensionScoring")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="body2" color="text.secondary">
                  {t("AIInterviewCoachHome.trustChipCompanySpecificCalibration")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="body2" color="text.secondary">
                  {t("AIInterviewCoachHome.trustChipFreeEvaluationsPerMonth", {
                    count: FREE_EVALS_PER_MONTH,
                  })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* The 6 dimensions */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1} mb={5}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("AIInterviewCoachHome.howItScoresEyebrow")}
          </Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("AIInterviewCoachHome.sixDimensionsTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 780, lineHeight: 1.65 }}>
            {t("AIInterviewCoachHome.sixDimensionsIntro")}
          </Typography>
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 3 }}>
          {SCORING_DIMENSION_KEYS.map((d) => (
            <Paper
              key={d.title}
              elevation={0}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 3,
                p: 3,
              }}
            >
              <Typography variant="h6" fontWeight={500} gutterBottom>
                {t(d.title)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                {t(d.body)}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Sample evaluation — proof of what an evaluation looks like */}
        <Box mt={{ xs: 6, md: 8 }}>
          <Stack spacing={1} mb={4}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              {t("AIInterviewCoachHome.seeItInActionEyebrow")}
            </Typography>
            <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
              {t("AIInterviewCoachHome.concreteFeedbackTitle")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 780, lineHeight: 1.65 }}>
              {t("AIInterviewCoachHome.concreteFeedbackIntro")}
            </Typography>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Card header */}
            <Box
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 1.5, md: 2 }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                  <Chip
                    label={t("AIInterviewCoachHome.calibratedForLabel", {
                      company: SAMPLE_EVALUATION.targetCompany,
                    })}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      letterSpacing: "0.06em",
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Chip
                    label={t("AIInterviewCoachHome.sampleAnonymizedChipLabel")}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      letterSpacing: "0.06em",
                      bgcolor: "transparent",
                      border: `1px solid ${theme.palette.divider}`,
                      color: "text.secondary",
                    }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t("AIInterviewCoachHome.evaluatedInSecondsLabel", {
                    seconds: SAMPLE_EVALUATION.evaluatedInSeconds,
                  })}
                </Typography>
              </Stack>
            </Box>

            {/* Question + answer */}
            <Box sx={{ p: { xs: 2.5, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}
              >
                {t("AIInterviewCoachHome.questionLabel")}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 2.5, mt: 0.5 }}>
                {t(SAMPLE_EVALUATION.questionKey)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}
              >
                {t("AIInterviewCoachHome.candidatesAnswerLabel")}
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  p: 2,
                  bgcolor: "background.default",
                  borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", lineHeight: 1.7 }}>
                  "{t(SAMPLE_EVALUATION.answerExcerptKey)}"
                </Typography>
              </Box>
            </Box>

            {/* Hiring signal */}
            <Box sx={{ p: { xs: 2.5, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}
              >
                {t("AIInterviewCoachHome.hiringSignalLabel")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "baseline" }} mt={0.5}>
                <Typography variant="h5" fontWeight={500} sx={{ color: theme.palette.primary.main, letterSpacing: "-0.01em" }}>
                  {t(SAMPLE_EVALUATION.hiringDecisionKey)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(SAMPLE_EVALUATION.hiringSubtextKey)}
                </Typography>
              </Stack>
            </Box>

            {/* Per-dimension scores */}
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary", mb: 2, display: "block" }}
              >
                {t("AIInterviewCoachHome.dimensionScoresLabel")}
              </Typography>
              <Stack spacing={2.5}>
                {SAMPLE_EVALUATION.dimensions.map((d) => {
                  const color = scoreColor(d.score, theme);
                  return (
                    <Box key={d.nameKey}>
                      <Stack direction="row" alignItems="baseline" spacing={1} mb={0.75} flexWrap="wrap">
                        <Typography variant="body2" fontWeight={500}>
                          {t(d.nameKey)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 500, color, letterSpacing: "0.04em", ml: "auto" }}
                        >
                          {d.score}/5
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          width: "100%",
                          height: 6,
                          bgcolor: alpha(color, 0.15),
                          borderRadius: 3,
                          overflow: "hidden",
                          mb: 1,
                        }}
                      >
                        <Box sx={{ width: `${(d.score / 5) * 100}%`, height: "100%", bgcolor: color, transition: "width 0.3s" }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {t(d.feedbackKey)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {/* Takeaway */}
            <Box
              sx={{
                p: { xs: 2.5, md: 3 },
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.palette.primary.main }}
              >
                {t("AIInterviewCoachHome.takeawayLabel")}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, lineHeight: 1.65 }}>
                {t(SAMPLE_EVALUATION.takeawayKey)}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Features */}
        <Box mt={{ xs: 6, md: 8 }}>
          <Stack spacing={1} mb={4}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              {t("AIInterviewCoachHome.whatYouGetEyebrow")}
            </Typography>
            <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
              {t("AIInterviewCoachHome.practiceWayTitle")}
            </Typography>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 3 }}>
            {FEATURE_KEYS.map((f) => (
              <Paper
                key={f.title}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mt: 0.25, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={500} gutterBottom>
                      {t(f.title)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      {t(f.body)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Cross-link to Interview Playbook */}
        <Box mt={{ xs: 6, md: 8 }}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: "text.secondary", letterSpacing: "0.08em", textTransform: "uppercase" }}
              >
                {t("AIInterviewCoachHome.technicalCompanionEyebrow")}
              </Typography>
              <Typography variant="h6" fontWeight={500} sx={{ mt: 0.5, mb: 0.5 }}>
                {t("AIInterviewCoachHome.technicalCompanionTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
                {t("AIInterviewCoachHome.technicalCompanionBody")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/agentic-interviewing")}
              sx={{ fontWeight: 500, flexShrink: 0 }}
            >
              {t("AIInterviewCoachHome.openPlaybookButton")}
            </Button>
          </Paper>
        </Box>
      </Container>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            p: { xs: 4, md: 6 },
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 3,
          }}
        >
          <RecordVoiceOverIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h4" fontWeight={500} gutterBottom sx={{ letterSpacing: "-0.02em" }}>
            {t("AIInterviewCoachHome.finalCtaTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: "auto", mb: 4 }}>
            {t("AIInterviewCoachHome.finalCtaBody")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={() =>
              navigate(
                primaryCta.href,
                primaryCta.href === "/login"
                  ? { state: { from: "/evaluate" } }
                  : undefined
              )
            }
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {primaryCta.label}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
